import React, { useContext, useMemo } from 'react';
import { SerializedStyles } from '@emotion/react';
import {
  GraphIcon,
  RatingIcon,
  HandshakeOutlineIcon,
} from '@domain-group/fe-co-icon';
import { MediaContext } from 'react-media-query-hoc';
import { SearchAutocomplete } from '../..';
import type { Option } from '../../search-autocomplete/types';
import * as bannerStyles from './landing-page-banner-2026.style';

interface LandingPageBanner2026Styles {
  landing: SerializedStyles;
  wrapper: SerializedStyles;
  content: SerializedStyles;
  search: SerializedStyles;
}

interface LandingPageBannerProps {
  skipLinkContentId: string;
  styles: LandingPageBanner2026Styles;
  onSuggestionSelect: (params: {
    item: Option;
    position: number;
    textTyped: string;
    totalSuggestions?: number;
  }) => void;
  onSearchInputInteraction: () => void;
  onTapSearch: (options: Option[]) => void;
}

const TRUST_SIGNALS = [
  { label: 'Agent sales data', Icon: GraphIcon },
  { label: 'Verified agent reviews', Icon: RatingIcon },
  { label: 'Nationwide coverage', Icon: HandshakeOutlineIcon },
];

const ANIMATED_PLACEHOLDER_DESKTOP = {
  staticText: 'Search suburb, agent or agency ',
  phrases: [
    'to sell your home',
    'to manage your property',
    'to get an appraisal',
  ],
};

const ANIMATED_PLACEHOLDER_MOBILE = {
  staticText: 'Search ',
  inactiveText: 'Search suburb, agent or agency',
  phrases: [
    'suburbs to sell your home',
    'agencies to lease your home',
    'agents to get an appraisal',
  ],
};

const LandingPageBanner2026 = ({
  skipLinkContentId,
  styles,
  onSuggestionSelect,
  onSearchInputInteraction,
  onTapSearch,
}: LandingPageBannerProps): JSX.Element => {
  const media = useContext<{ mobile?: boolean }>(MediaContext);
  const animatedPlaceholder = useMemo(
    () =>
      media?.mobile
        ? ANIMATED_PLACEHOLDER_MOBILE
        : ANIMATED_PLACEHOLDER_DESKTOP,
    [media?.mobile],
  );

  return (
    <div css={styles.landing}>
      <div css={styles.wrapper}>
        <div css={styles.content} id={skipLinkContentId}>
          <h1 css={bannerStyles.title}>
            <span>Find real estate agents</span>
            <br />
            <span css={bannerStyles.subTitle}>with confidence</span>
          </h1>
          <div css={styles.search}>
            <SearchAutocomplete
              recentSearches
              icons
              isLocationSearchByAgent
              useTypeaheadV5
              animatedPlaceholder={animatedPlaceholder}
              suggestedSuburbs
              onSuggestionSelect={onSuggestionSelect}
              onInputInteraction={onSearchInputInteraction}
              onSearchExecuted={onTapSearch}
            />
          </div>
          <div css={bannerStyles.trustSignals}>
            {TRUST_SIGNALS.map(({ label, Icon }) => (
              <span key={label} css={bannerStyles.trustItem}>
                <Icon size="small" />
                <span css={bannerStyles.trustItemLabel}>{label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageBanner2026;
